import { MoreOutlined, PlusOutlined } from '@ant-design/icons';
import { Tabs } from 'antd';
import isEqual from 'lodash/isEqual';
import { useMemo, useState } from 'react';

import { Button } from '@/components-ant/button';
import { FilterSelections } from '@/components-ant/data-table/filter-panel/types';
import { Result } from '@/shared/helpers/Result';
import { removeNullOrUndefinedKeys } from '@/shared/helpers/object';
import { HorizontallySpaced } from '@/styles/layout';

import { AddViewModal } from './AddViewModal';
import { ViewConfigurationModal } from './ViewConfigurationModal';
import { ButtonWrapper, CancelButton, TabsAndAddWrapper, Wrapper } from './styles';
import { AddViewInput, UpdateViewInput, View, ViewTab } from './types';

type BaseProps<FilterKeyT extends string> = {
  views: View<FilterKeyT>[];
  current?: string;
  onChange: (viewId: string) => void;
};

type Props<FilterKeyT extends string> = BaseProps<FilterKeyT> &
  (
    | {
        canManageViews?: false;
        filterSelections?: never;
        onAddView?: never;
        onUpdateView?: never;
        onDeleteView?: never;
        onResetFilters?: never;
      }
    | {
        canManageViews: true;
        filterSelections?: FilterSelections<FilterKeyT>;

        // using the minimum needed return types for these
        onAddView: (input: AddViewInput<FilterKeyT>) => Promise<Result<Pick<ViewTab, 'id'>>>;
        onUpdateView: (input: UpdateViewInput<FilterKeyT>) => Promise<Result>;
        onDeleteView: (viewId: string) => Promise<Result>;
        onResetFilters?: () => void;
      }
  );

export function ViewTabs<FilterKeyT extends string = string>({
  views,
  current = views[0]?.id,
  onChange,
  filterSelections,
  canManageViews,
  onAddView,
  onUpdateView,
  onDeleteView,
  onResetFilters,
}: Props<FilterKeyT>) {
  const [isConfigurationModalOpen, setIsConfigurationModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const currentView = useMemo(() => views.find((view) => view.id === current), [views, current]);
  const hasUnsavedChanges = useMemo(
    () =>
      !isEqual(
        // need to remove null values as they're not actual filters (but may be there to override view defaults)
        removeNullOrUndefinedKeys(currentView?.filters ?? {}),
        removeNullOrUndefinedKeys(filterSelections ?? {}),
      ),
    [filterSelections, currentView],
  );

  const addView = async (name: string) => {
    if (!canManageViews) return Promise.resolve(Result.ok());
    const result = await onAddView({ name, filters: filterSelections ?? {} });
    if (result.isSuccess && result.value) {
      // switch to the newly created view
      onChange(result.value.id);
    }
    return result;
  };

  const saveFilters = async () => {
    const view = views.find((v) => v.id === current);
    if (!canManageViews || !view) return;

    setIsUpdating(true);
    await onUpdateView({
      id: view.id,
      filters: filterSelections ?? {},
    });
    setIsUpdating(false);
  };

  const deleteView = async (viewId: string) => {
    if (!canManageViews) return Promise.resolve(Result.ok());
    const result = await onDeleteView(viewId);
    if (result.isSuccess && current === viewId) {
      // switch to the first view if the current view is deleted
      onChange(views[0]?.id ?? '');
    }
    return result;
  };

  return (
    <Wrapper>
      <TabsAndAddWrapper>
        <Tabs
          onTabClick={(key) => onChange(key)}
          activeKey={current}
          items={views.map(({ id, title }) => ({ key: id, label: title }))}
        />
        {canManageViews && (
          <ButtonWrapper>
            <Button
              type="text"
              icon={<PlusOutlined />}
              aria-label="Add new view"
              onClick={() => setIsAddModalOpen(true)}
            />
          </ButtonWrapper>
        )}
      </TabsAndAddWrapper>

      {canManageViews && (
        <ButtonWrapper>
          <HorizontallySpaced $factor={0}>
            {hasUnsavedChanges && (
              <>
                {!currentView?.isSaved ? (
                  <Button type="link" onClick={() => setIsAddModalOpen(true)}>
                    Save as view
                  </Button>
                ) : (
                  <Button type="link" onClick={saveFilters} loading={isUpdating}>
                    Save view
                  </Button>
                )}
                <CancelButton type="link" onClick={onResetFilters}>
                  Cancel
                </CancelButton>
              </>
            )}
            <Button
              type="text"
              icon={<MoreOutlined />}
              onClick={() => setIsConfigurationModalOpen(true)}
              aria-label="Configure views"
            />
          </HorizontallySpaced>
        </ButtonWrapper>
      )}

      {canManageViews && (
        <AddViewModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onSubmit={addView}
        />
      )}

      {canManageViews && (
        <ViewConfigurationModal
          isOpen={isConfigurationModalOpen}
          onClose={() => setIsConfigurationModalOpen(false)}
          savedViews={views.filter((view) => view.isSaved)}
          onUpdateView={onUpdateView}
          onDeleteView={deleteView}
        />
      )}
    </Wrapper>
  );
}

ViewTabs.displayName = 'DataTable.ViewTabs';
